import { useMemo } from "react";

import {
  getDecorationPaintSetById,
  getCatalogDecorationById,
  getPaintSetIdForDecoration,
  isDynamicDecorationPaintSet,
  isOrderedDecorationPaintSet,
  resolveTileAssetUrl,
  useTileCatalogStore,
} from "@west-marches/shared";
import {
  DECORATION_SET_DYNAMIC_DESCRIPTION,
  DECORATION_SET_ORDERED_DESCRIPTION,
  TILE_SET_DYNAMIC_BADGE,
  TILE_SET_ORDERED_BADGE,
} from "@/lib/tileSetUiLabels";

import {
  DEFAULT_DECORATION_SCATTER_DENSITY,
  DEFAULT_DECORATION_SCATTER_RADIUS,
  MAX_DECORATION_SCATTER_DENSITY,
  MAX_DECORATION_SCATTER_RADIUS,
  MIN_DECORATION_SCATTER_DENSITY,
  MIN_DECORATION_SCATTER_RADIUS,
  useEditorStore,
} from "@/state/useEditorStore";
import { getMapChromeTheme } from "@/components/mapChromeTheme";
import { DynamicBrushBadge } from "./DynamicBrushBadge";
import SelectionCarousel from "./SelectionCarousel";

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

export default function SelectedDecorationStrip() {
  const theme = getMapChromeTheme();
  const catalogRevision = useTileCatalogStore((state) => state.revision);
  const selectedDecorationId = useEditorStore((state) => state.selectedDecorationId);
  const selectedDecorationTaxonomyNodeId = useEditorStore(
    (state) => state.selectedDecorationTaxonomyNodeId,
  );
  const selectDecoration = useEditorStore((state) => state.selectDecoration);
  const clearSelectedDecoration = useEditorStore((state) => state.clearSelectedDecoration);
  const decorationMode = useEditorStore((state) => state.decorationMode);
  const decorationScatterRadius = useEditorStore((state) => state.decorationScatterRadius);
  const decorationScatterDensity = useEditorStore((state) => state.decorationScatterDensity);
  const setDecorationMode = useEditorStore((state) => state.setDecorationMode);
  const setDecorationScatterRadius = useEditorStore((state) => state.setDecorationScatterRadius);
  const setDecorationScatterDensity = useEditorStore((state) => state.setDecorationScatterDensity);
  const decoration = useMemo(
    () => (selectedDecorationId ? getCatalogDecorationById(selectedDecorationId) : null),
    [selectedDecorationId, catalogRevision],
  );

  if (!selectedDecorationId || !decoration) {
    return null;
  }

  const decorationUrl = decoration.url ?? resolveTileAssetUrl(decoration.relativePath);
  const selectedPaintSetId =
    decoration.paintSetId ?? getPaintSetIdForDecoration(selectedDecorationId);
  const selectedPaintSet = selectedPaintSetId
    ? getDecorationPaintSetById(selectedPaintSetId)
    : undefined;
  const dynamicLook = selectedPaintSet ? isDynamicDecorationPaintSet(selectedPaintSet) : false;
  const orderedLook = selectedPaintSet ? isOrderedDecorationPaintSet(selectedPaintSet) : false;

  if (orderedLook) {
    return null;
  }

  return (
    <SelectionCarousel
      anchorClassName="bottom-32"
      panelClassName="w-fit max-w-[92vw]"
      topRightOverlay={
        <button
          type="button"
          onClick={clearSelectedDecoration}
          aria-label="Close decoration toolbar"
          title="Close decoration toolbar"
          className={`absolute top-0 right-0 z-20 inline-flex h-8 w-8 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full border-[3px] border-neutral-950 bg-[#fffaf1] text-neutral-950 shadow-sm transition-colors hover:bg-[#fff2d9]`}
        >
          <CloseIcon />
        </button>
      }
      headerLeft={
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 rounded-xl p-1 ${theme.subtlePanel}`}>
            <button
              type="button"
              onClick={() => setDecorationMode("single")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                decorationMode === "single"
                  ? theme.actionButtonActive
                  : theme.actionButtonIdle
              }`}
            >
              Single <span className="ml-1 opacity-70">(1)</span>
            </button>
            <button
              type="button"
              disabled
              aria-disabled="true"
              title="Brush mode is coming soon"
              className={`cursor-not-allowed rounded-lg px-4 py-2 text-sm font-medium opacity-45 transition-colors ${theme.actionButtonIdle}`}
            >
              Brush <span className="ml-1 opacity-70">(soon)</span>
            </button>
            <button
              type="button"
              onClick={() => setDecorationMode("erase")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                decorationMode === "erase"
                  ? theme.actionButtonActive
                  : theme.actionButtonIdle
              }`}
            >
              Erase <span className="ml-1 opacity-70">(3)</span>
            </button>
          </div>
          {decorationMode === "brush" ? (
            <>
              <div className={`flex items-center gap-3 px-2.5 py-1.5 ${theme.subtlePanel}`}>
                <label className={`flex min-w-[110px] flex-col gap-0.5 text-[11px] ${theme.textMuted}`}>
                  <span className="flex items-center justify-between gap-2">
                    <span>Radius</span>
                    <span className={`tabular-nums ${theme.textSubtle}`}>{decorationScatterRadius}px</span>
                  </span>
                  <input
                    type="range"
                    min={MIN_DECORATION_SCATTER_RADIUS}
                    max={MAX_DECORATION_SCATTER_RADIUS}
                    step={1}
                    value={decorationScatterRadius}
                    onChange={(e) => setDecorationScatterRadius(Number(e.currentTarget.value))}
                  />
                </label>
                <label className={`flex min-w-[110px] flex-col gap-0.5 text-[11px] ${theme.textMuted}`}>
                  <span className="flex items-center justify-between gap-2">
                    <span>Density</span>
                    <span className={`tabular-nums ${theme.textSubtle}`}>{decorationScatterDensity}</span>
                  </span>
                  <input
                    type="range"
                    min={MIN_DECORATION_SCATTER_DENSITY}
                    max={MAX_DECORATION_SCATTER_DENSITY}
                    step={1}
                    value={decorationScatterDensity}
                    onChange={(e) => setDecorationScatterDensity(Number(e.currentTarget.value))}
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDecorationScatterRadius(DEFAULT_DECORATION_SCATTER_RADIUS);
                  setDecorationScatterDensity(DEFAULT_DECORATION_SCATTER_DENSITY);
                }}
                className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${theme.actionButtonIdle}`}
              >
                Reset
              </button>
            </>
          ) : null}
        </div>
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={clearSelectedDecoration}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${theme.actionButtonIdle}`}
          >
            Clear
          </button>
        </div>
      }
      viewportClassName="scrollbar-none min-h-[88px]"
      viewportStyle={{ scrollbarWidth: "none" }}
      trackClassName="mx-auto items-center gap-4 py-1"
    >
      <button
        type="button"
        onClick={(event) => {
          selectDecoration(
            selectedDecorationId,
            selectedDecorationTaxonomyNodeId ?? decoration.taxonomyNodeIds[0] ?? null,
          );
          event.currentTarget.blur();
        }}
        className={`group relative h-[64px] w-[64px] shrink-0 snap-center overflow-hidden rounded-xl transition-all ${theme.cardSelectedRing}`}
        aria-pressed
        title={decoration.title}
      >
        <img
          src={decorationUrl}
          alt={decoration.title}
          className="absolute inset-0 h-full w-full object-contain p-2.5 transition-all"
          loading="lazy"
        />
        {dynamicLook || orderedLook ? (
          <DynamicBrushBadge
            compact
            className="absolute right-1 top-1 text-[10px]"
            onActivate={() => {
              selectDecoration(
                selectedDecorationId,
                selectedDecorationTaxonomyNodeId ?? decoration.taxonomyNodeIds[0] ?? null,
              );
            }}
            label={dynamicLook ? TILE_SET_DYNAMIC_BADGE : TILE_SET_ORDERED_BADGE}
            description={
              dynamicLook
                ? DECORATION_SET_DYNAMIC_DESCRIPTION
                : DECORATION_SET_ORDERED_DESCRIPTION
            }
          />
        ) : null}
      </button>
    </SelectionCarousel>
  );
}
